import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  tap,
  finalize,
  of,
} from 'rxjs';
import { ColDef } from 'ag-grid-community';
import { TableConfig } from '../../../shared/components/table/table.interfaces';
import { ActionConfig } from '../../../shared/components/table/table-action-configs';
import {
  OFFBOARDING_COLUMNS,
  OFFBOARDING_TABLE_CONFIG,
  OFFBOARDING_ACTIONS,
} from '../asset-management-table-configuration';
import { TableActionHandler } from './table-action-handler.interface';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { AddCommentsComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/add-comments/add-comments.component';
import { ViewCommentsComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/view-comments/view-comments.component';
import { ResourceProfilePageComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/resource-profile-page/resource-profile-page.component';
import { OffboardingRequestComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/offboarding-request/offboarding-request.component';
import { AssetManagementService } from '../asset-management-service';
import { ToastrService } from 'ngx-toastr';
import { DateUtils, getMonthDateRange } from '@app/shared/utils/date.utils';
import { formatSingleDate } from '@app/shared/utils/utils';
import { yearDateFormat } from '@app/shared/constants/dateFormat';

export interface OffboardingRequest {
  id: number;
  employeeId: number;
  employee: string;
  empId: string;
  jobTitle: string;
  email: string;
  rm: string;
  leader: string;
  company: string;
  location: string;
  laptop: string;
  laptopSerialNumber: string;
  chargerSerialNumber: string;
  accessories: string;
  requestedBy: string;
  dateOfReturn: string;
  dateOfOffboarding: string;
  status:
    | 'In Progress'
    | 'Completed'
    | 'Pending'
    | 'Cancelled'
    | 'Asset Collection Scheduled';
  comments: string;
  employeeStatus: 'Active' | 'Inactive' | 'Terminated';
  assetReturnStatus: 'Pending' | 'Collected' | 'Not Required';
  isComment?: boolean;
}

@Injectable({
  providedIn: 'root',
})

export class OffboardingRequestsTableService
  implements TableActionHandler<OffboardingRequest>
{
  handleAction(action: string, data: OffboardingRequest): void {
    // No-op: required by TableActionHandler interface
  }
  private readonly dataSubject = new BehaviorSubject<OffboardingRequest[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly selectedRowsSubject = new BehaviorSubject<
    OffboardingRequest[]
  >([]);

  public readonly data$ = this.dataSubject.asObservable();
  public readonly loading$ = this.loadingSubject.asObservable();
  public readonly selectedRows$ = this.selectedRowsSubject.asObservable();

  public readonly columns: ColDef[] = OFFBOARDING_COLUMNS;
  public readonly tableConfig: TableConfig = OFFBOARDING_TABLE_CONFIG;
  public readonly actions: ActionConfig[] = OFFBOARDING_ACTIONS;

  monthSelected: string = '';
  yearSelected: string = '';
    offboardingData: OffboardingRequest[] = [];
    // Track last requested date range so refreshData() works
    currentStartDate: string = '';
  currentEndDate: string = '';
  
  constructor(
    private readonly dialog: MatDialog,
    private readonly assetMgmtService: AssetManagementService,
    private toasterService: ToastrService,
  ) {
    (window as any).openComments = (id: string | number) => {
      const idStr = id.toString();
      const record = this.dataSubject.value.find(
        (r) => (r.id && r.id.toString() === idStr) || (r.empId && r.empId.toString() === idStr)
      );
      if (!record) {
        this.toasterService.error('Offboarding record not found for Comments');
        return;
      }
      const hasComments = typeof record.isComment === 'boolean' ? record.isComment : false;
      const dialogConfig: MatDialogConfig = {
        width: '720px',
        maxWidth: '80vw',
        panelClass: ['comments-dialog-panel', 'center-dialog'],
        autoFocus: false,
        disableClose: false,
      };
      if (hasComments) {
        this.fetchAndShowComments(record, dialogConfig);
      } else {
        this.openAddCommentsDialog(record, dialogConfig);
      }
    };
    // Expose global helper for ResourceProfile dialog
    (window as any).openResourceProfileOffboarding = (id: string | number) => {
      if (typeof id === 'number') {
        this.openResourceProfileDialog(id);
        return;
      }
      const parsed = parseInt(id as string, 10);
      if (!Number.isNaN(parsed)) {
        this.openResourceProfileDialog(parsed);
        return;
      }
      const record = this.offboardingData?.find(r => (r as any).employeeId === id || r.empId === id);
      if (record) {
        this.openResourceProfileDialog(record.employeeId);
      } else {
        this.toasterService.error('Employee record not found for Resource Profile');
      }
    };
  }

  loadInitialData(): void {
    const { startDate, endDate } = getMonthDateRange(history.state.selectedMonth, yearDateFormat.date);
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    this.loadOffboardingData(this.currentStartDate, this.currentEndDate);
  }

  loadOffboardingData(startDate: string, endDate: string): void {
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    this.assetMgmtService.getOffboardingData(startDate, endDate).subscribe({
      next: (response) => {
        const mappedData = (response.data || []).map((item: any, idx: number) => ({
          ...item,
          id: item.employeeId || idx + 1,
          empId: item.employeeId
        }));
        this.offboardingData = mappedData;
        this.dataSubject.next(mappedData);
      },
    });
  }

  refreshData(): void {
    const { startDate, endDate } = getMonthDateRange(history.state.selectedMonth, yearDateFormat.date);
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    this.loadOffboardingData(this.currentStartDate, this.currentEndDate);
  }

  getOffboardingById(employeeId: string | number): OffboardingRequest | undefined {
    const idStr = employeeId.toString();
    return this.dataSubject.value.find(
      (item) => item.employeeId?.toString() === idStr
    );
  }

  getRecordsByStatus(
    status: OffboardingRequest['status'],
  ): OffboardingRequest[] {
    return this.dataSubject.value.filter((item) => item.status === status);
  }

  openDialog(employeeId: number | string): void {
    const record = this.dataSubject.value.find((r) => r.id === employeeId || r.empId === employeeId);
    if (!record) {
      return;
    }
    const dialogConfig: MatDialogConfig = {
      width: '720px',
      maxWidth: '80vw',
      panelClass: ['comments-dialog-panel', 'center-dialog'],
      autoFocus: false,
      disableClose: false,
    };
    this.fetchAndShowComments(record, dialogConfig);
  }

    openResourceProfileDialog(employeeId: number): void {
      const record = this.getOffboardingById(employeeId);

      console.log('Opening Resource Profile for employeeId:', employeeId, record);
      if (!record) {
        return;
      }
      const dialogConfig: MatDialogConfig = {
        width: '1400px',
        height: '900px',
        panelClass: ['resource-profile-dialog-panel', 'center-dialog'],
        autoFocus: false,
        disableClose: false,
        data: { record},
      };
      this.dialog.open(ResourceProfilePageComponent, dialogConfig);
    }

  private async fetchAndShowComments(record: any, dialogConfig: MatDialogConfig) {
    this.assetMgmtService.offboardingComments({ employeeId: record.empId || record.id?.toString() }).subscribe({
      next: (response) => {
        if (Array.isArray(response) && response.length > 0) {
          record.allComments = response;
        } else if (response?.commentText) {
          record.allComments = [response];
        } else {
          record.allComments = [];
        }
        this.openViewCommentsDialog(record, dialogConfig);
      },
      error: () => {
        this.toasterService.error('Failed to load comments.');
        this.openViewCommentsDialog(record, dialogConfig);
      }
    });
  }

  private openViewCommentsDialog(
    record: any,
    dialogConfig: MatDialogConfig,
  ): void {
    const dialogRef = this.dialog.open(ViewCommentsComponent, {
      ...dialogConfig,
      data: { record },
    });
    const compInstance: any = dialogRef.componentInstance;
    if (compInstance?.addComments) {
      compInstance.addComments.subscribe(() => {
        dialogRef.close();
        this.openAddCommentsDialog(record, dialogConfig);
      });
    }
  }

  private openAddCommentsDialog(
    record: any,
    dialogConfig: MatDialogConfig,
  ): void {
    const dialogRef = this.dialog.open(AddCommentsComponent, {
      data: { record },
      ...dialogConfig,
    });
    const compInstance: any = dialogRef.componentInstance;
    if (compInstance?.save) {
      compInstance.save.subscribe((newComment: string) => {
        dialogRef.close();
        this.assetMgmtService.offboardingComments({
          assetId: record.assetId || record.id?.toString(),
          employeeId: record.empId || record.id?.toString(),
          commentText: newComment
        }).subscribe({
          next: () => {
            record.comments = newComment;
            record.isComment = true;
            const updatedData = this.dataSubject.value.map((r) =>
              (r.id?.toString() === (record.id?.toString() || record.empId?.toString()))
                ? { ...r, comments: newComment, isComment: true }
                : r
            );
            this.dataSubject.next(updatedData);
            this.toasterService.success('Comment added successfully.');
          },
          error: () => {
            this.toasterService.error('Failed to add comment.');
          }
        });
      });
    }
    if (compInstance?.cancel) {
      compInstance.cancel.subscribe(() => dialogRef.close());
    }
  }

  openOffboardingFormDialog(): void {
     const dialogRef: MatDialogRef<OffboardingRequestComponent> = this.dialog.open(
          OffboardingRequestComponent,
          {
            // width: '1000px',
            // maxWidth: '90vw',
            // minHeight: '250px',
            // maxHeight: '90vh',
            disableClose: false,
            panelClass: 'offboarding-request-modal',
            data: {
              isModal: true,
              isEditMode: false,
            },
          },
        );
    
        dialogRef.afterClosed().subscribe((result: any) => {
          console.log('The dialog was closed');
          if (result !== undefined) {
          }
        });
  }
}
